import type { KeyboardEvent, MouseEvent } from 'react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { Trash2 } from 'lucide-react';
import type { AppNotification } from '@/hooks/useNotifications';
import {
  formatNotificationTimeCompact,
  formatNotificationTimeRelative,
  notificationIconForType,
} from '@/utils/notificationDisplay';
import { SwipeToDelete } from './SwipeToDelete';
import styles from './NotificationRow.module.css';

interface NotificationRowProps {
  notification: AppNotification;
  onActivate: (n: AppNotification) => void;
  onDelete: (id: string) => void;
  swipeOpen: boolean;
  onSwipeOpenChange: (open: boolean) => void;
  /** Being deleted (useRowRemoval): slides out, then collapses. */
  exiting?: boolean;
}

export function NotificationRow({
  notification: n,
  onActivate,
  onDelete,
  swipeOpen,
  onSwipeOpenChange,
  exiting = false,
}: NotificationRowProps) {
  const { Icon, color } = notificationIconForType(n.type);
  // A chat row is rewritten in place as the thread continues, so updated_at is what
  // the list is ordered by and what the timestamp has to show.
  const at = n.updated_at ?? n.created_at;

  const activate = () => {
    if (swipeOpen) {
      onSwipeOpenChange(false);
      return;
    }
    onActivate(n);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate();
    }
  };

  const handleDelete = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onDelete(n.id);
  };

  return (
    <div
      className={styles.collapse}
      data-exiting={exiting || undefined}
      aria-hidden={exiting || undefined}
    >
      <div className={styles.collapseInner}>
        <SwipeToDelete
          open={swipeOpen}
          exiting={exiting}
          onOpenChange={onSwipeOpenChange}
          onDelete={() => onDelete(n.id)}
        >
          <div className={styles.row} data-unread={n.read ? undefined : ''}>
            <Flex
              role="button"
              tabIndex={0}
              aria-label={n.title}
              flex="1"
              minW="0"
              align="flex-start"
              gap="3"
              px="4"
              py="3.5"
              cursor="pointer"
              onClick={activate}
              onKeyDown={handleKeyDown}
              _focusVisible={{
                outline: '2px solid',
                outlineColor: 'volyx.action',
                outlineOffset: '-2px',
              }}
            >
              <Flex
                flexShrink={0}
                w="40px"
                h="40px"
                borderRadius="full"
                bg={`${color}1F`}
                align="center"
                justify="center"
              >
                <Icon size={20} color={color} strokeWidth={2} aria-hidden />
              </Flex>
              <Box flex="1" minW="0">
                <Flex align="flex-start" gap="2">
                  <Text
                    flex="1"
                    minW="0"
                    m="0"
                    fontSize="15px"
                    lineHeight="20px"
                    fontWeight="semibold"
                    color="volyx.text"
                    lineClamp={2}
                    wordBreak="break-word"
                  >
                    {n.title}
                  </Text>
                  <Flex align="center" gap="1.5" flexShrink={0} pt="1px">
                    <chakra.time
                      dateTime={at}
                      title={formatNotificationTimeCompact(at)}
                      fontSize="13px"
                      lineHeight="18px"
                      color="volyx.textSecondary"
                      fontVariantNumeric="tabular-nums"
                    >
                      {formatNotificationTimeRelative(at)}
                    </chakra.time>
                    {!n.read && (
                      <Box
                        data-testid="unread-dot"
                        role="img"
                        aria-label="Unread"
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg="volyx.action"
                      />
                    )}
                  </Flex>
                </Flex>
                <Text
                  m="0"
                  mt="1"
                  fontSize="14px"
                  lineHeight="20px"
                  color="volyx.textMuted"
                  lineClamp={2}
                  wordBreak="break-word"
                >
                  {n.message}
                </Text>
              </Box>
            </Flex>
            <button
              type="button"
              className={styles.delete}
              aria-label="Delete notification"
              onClick={handleDelete}
            >
              <Trash2 size={18} aria-hidden />
            </button>
          </div>
        </SwipeToDelete>
      </div>
    </div>
  );
}
